import {
  SYSTEM_CURRENCIES,
  toFixedNoRound,
  CALCULATION_RESULT_PRECISION,
  PLATFORM_FEE_CURRENCY,
} from '@okletsplay/core';

export const SUPPORTED_CRYPTO_LABELS = SYSTEM_CURRENCIES.join(', ');

/**
 * @deprecated Use MAIN_CURRENCY, PLATFORM_FEE_CURRENCY, SYSTEM_CURRENCIES from '@okletsplay/core' instead.
 */
export const CRYPTO_LABEL = 'OKLP';

const FIAT_CURRENCY = 'USD';

export type FiatCurrencyDisplay = 'symbol' | 'code';

export const DEFAULT_FORMAT_CURRENCY_PRECISION = 2;
export const HIGH_FORMAT_CURRENCY_PRECISION = CALCULATION_RESULT_PRECISION;

const HIGH_FORMAT_CURRENCY_THRESHOLD = 0.01;
const MIN_FIAT_DISPLAY_AMOUNT = 0.01;

export const formatFiat = (rawAmount: number, currencyDisplay: FiatCurrencyDisplay = 'symbol') => {
  const amount = rawAmount > 0 ? Math.max(rawAmount, MIN_FIAT_DISPLAY_AMOUNT) : rawAmount;

  const prefixSign = rawAmount > 0 && rawAmount < MIN_FIAT_DISPLAY_AMOUNT ? '<' : '';

  const formattedAmount = toFixedNoRound(amount, DEFAULT_FORMAT_CURRENCY_PRECISION)
    .toLocaleString('en', {
      style: 'currency',
      currency: FIAT_CURRENCY,
      currencyDisplay,
    })
    .replace(/^([A-Z]{3})\s*(.+)$/, '$2 $1');

  return `${prefixSign}${formattedAmount}`;
};

export const formatCurrency = (amount: number, currencyLabel = '', maxPrecision?: number) => {
  let maximumFractionDigits: number;

  if (maxPrecision) {
    maximumFractionDigits = maxPrecision;
  } else {
    maximumFractionDigits =
      amount < HIGH_FORMAT_CURRENCY_THRESHOLD
        ? HIGH_FORMAT_CURRENCY_PRECISION
        : DEFAULT_FORMAT_CURRENCY_PRECISION;
  }

  return [
    toFixedNoRound(amount, maximumFractionDigits).toLocaleString('en', {
      minimumFractionDigits: DEFAULT_FORMAT_CURRENCY_PRECISION,
      maximumFractionDigits: maximumFractionDigits,
    }),
    currencyLabel,
  ]
    .filter(Boolean)
    .join(' ');
};

export const formatCrypto = (
  amount: number,
  currencyLabel = PLATFORM_FEE_CURRENCY as string,
  maxPrecision = DEFAULT_FORMAT_CURRENCY_PRECISION
) => formatCurrency(amount, currencyLabel, maxPrecision);

export const convertCryptoToUsd = (cryptoAmount: number, exchangeRate: number) => {
  return cryptoAmount * exchangeRate;
};

export const convertUsdToCrypto = (usdAmount: number, exchangeRate: number) => {
  return toFixedNoRound(usdAmount / exchangeRate, DEFAULT_FORMAT_CURRENCY_PRECISION);
};

export const convertCrypto = (amount: number, rate: number) => {
  return amount / rate;
};

export const convertReverseCrypto = (amount: number, rate: number) => {
  return amount * rate;
};

export const calculatePlatformFeeFromPrimaryAmount = (
  amount: number,
  rate: number,
  feePercentage: number
): number => {
  return convertCrypto((amount * feePercentage) / 100, rate);
};

export const formatCryptoAndFiatByCryptoAmount = (cryptoAmount: number, exchangeRate: number) => {
  const crypto = formatCrypto(cryptoAmount);
  const fiat = formatFiat(convertCryptoToUsd(cryptoAmount, exchangeRate));
  return `${crypto} (${fiat})`;
};

export const formatCurrencies = (
  cryptoAmount: number,
  fiatAmount: number,
  options?: { cryptoCurrency: string }
) => {
  const crypto = formatCrypto(cryptoAmount, options?.cryptoCurrency);
  const fiat = formatFiat(fiatAmount);
  return `${crypto} (${fiat})`;
};

export const calculateTokensWithDecimals = (amount: number | string, digits: number): string => {
  return Math.ceil(+amount * Math.pow(10, digits)).toString(10);
};
